/**
 * Contextual help content for the sidebar navigation (UX-HELP-001).
 *
 * Keyed by the nav item's `to` (or `href` for external links) from
 * Layout.jsx's NAV array. Each entry answers, in this order: what it is,
 * what it does, when to use it, and — where relevant — the role/permission
 * required and whether it's a read-only view or lets you take action.
 *
 * Content is static and human-authored; it must never describe internal
 * implementation details, secrets, or anything not already visible in the
 * product itself.
 */

const ADMIN_ONLY = "גלוי למנהלי מערכת (Admin) ובעלי מערכת (Owner) בלבד.";
const AGENTS_VIEW = "דורש הרשאת \"צפייה בסוכני AI\" (agents_view).";
const AGENTS_MANAGE = "דורש הרשאת \"ניהול סוכני AI\" (agents_manage).";

export const NAV_HELP = {
  "/dashboard": {
    what: "מסך הבית של המערכת.",
    does: "מציג תמונת מצב כללית — קריאות פתוחות, משימות ואירועים אחרונים.",
    when: "נקודת התחלה יומית להתמצאות מהירה במצב הכללי.",
    readOnly: true,
  },
  "/tickets": {
    what: "רשימת כל קריאות השירות (IT).",
    does: "פתיחה, מעקב, שיוך וטיפול בקריאות של לקוחות וטכנאים.",
    when: "העבודה השוטפת היומיומית של צוות התמיכה.",
    readOnly: false,
  },
  "/projects": {
    what: "ניהול פרויקטים מתמשכים מול לקוחות.",
    does: "מעקב אחר משימות, סטטוס ולוחות זמנים של פרויקט.",
    when: "עבודה שחורגת מקריאת שירות בודדת ודורשת מעקב לאורך זמן.",
    readOnly: false,
  },
  "/maintenance": {
    what: "פריטי תחזוקה מתוכננת ותקופתית.",
    does: "מעקב אחר בדיקות ותחזוקה שצריך לבצע אצל לקוחות.",
    when: "תכנון וביצוע עבודות תחזוקה מונעת.",
    readOnly: false,
  },
  "/reports": {
    what: "דוחות ותובנות על פעילות המערכת.",
    does: "מציג נתונים מרוכזים לצורך ניתוח וקבלת החלטות.",
    when: "כשצריך תמונה מסכמת ולא קריאה בודדת.",
    readOnly: true,
  },
  "/companies": {
    what: "רשימת חברות הלקוחות במערכת.",
    does: "ניהול פרטי לקוח: חברות, דומיינים משויכים ואנשי קשר.",
    when: "הוספה או עדכון של לקוח.",
    readOnly: false,
    role: ADMIN_ONLY,
  },
  "/contacts": {
    what: "אנשי קשר אצל הלקוחות.",
    does: "ניהול פרטי התקשרות של אנשי קשר בחברות הלקוח.",
    when: "עדכון מי איש הקשר הרלוונטי אצל לקוח.",
    readOnly: false,
    role: ADMIN_ONLY,
  },
  "/managed-domains": {
    what: "מעקב דומיינים בניהול המערכת.",
    does: "רישום דומיינים, ספקי DNS, ספקי דואר ותאריכי חידוש.",
    when: "כדי לוודא שדומיין לא יפוג בלי התראה מוקדמת.",
    readOnly: false,
    role: ADMIN_ONLY,
  },
  "/technicians": {
    what: "רשימת הטכנאים במערכת.",
    does: "ניהול משתמשי טכנאים והרשאותיהם הבסיסיות.",
    when: "הוספה, עדכון או השבתה של טכנאי.",
    readOnly: false,
    role: ADMIN_ONLY,
  },
  "/agents": {
    what: "סוכני ה-AI הפעילים במערכת.",
    does: "צפייה בסוכנים המוגדרים ובתפקיד של כל אחד מהם.",
    when: "כדי לראות אילו סוכני AI קיימים ומה תפקידם.",
    readOnly: true,
    role: AGENTS_VIEW,
  },
  "/agent-runs": {
    what: "מרכז הריצות והאישורים של סוכני AI.",
    does: "מעקב אחרי ריצות סוכן, ואישור או דחייה של פעולות שממתינות לאישור אנושי.",
    when: "כשיש ריצת AI שממתינה לאישור, או כדי לבדוק מה סוכן ביצע.",
    readOnly: false,
    role: AGENTS_VIEW,
  },
  "/command-center": {
    what: "תצוגת פיקוד ובקרה על מערך סוכני ה-AI.",
    does: "תצוגה חזותית (read-only) של סוכנים, מצבם ופעילותם; אינה מבצעת פעולות.",
    when: "כדי לקבל מבט-על מהיר על מה שקורה כרגע במערך ה-AI.",
    readOnly: true,
    role: AGENTS_VIEW,
  },
  "/system-map": {
    what: "מפת המערכת — הסבר על מבנה Prodigy.",
    does: "מציגה את שכבות המערכת, גבולות האבטחה והאישור האנושי, זרימת עבודה ומלאי טכנולוגיות, עם סימון מה פעיל היום ומה מתוכנן.",
    when: "כדי להבין איך המערכת עובדת ומה סוכני ה-AI רשאים לעשות.",
    readOnly: true,
  },
  "/help": {
    what: "מרכז העזרה של המערכת.",
    does: "חיפוש בנושאי עזרה מאושרים ושאלות על אופן פעולת המערכת; התשובות מצוטטות ממסמכי העזרה בלבד ומציינות מתי אין מידע.",
    when: "כשלא ברור מה מסך עושה, מי מאשר פעולה, או איך מתבצע תהליך.",
    readOnly: true,
  },
  "/agent-permissions": {
    what: "ניהול הרשאות לסוכני AI.",
    does: "הגדרה מי יכול לצפות, להפעיל, לאשר ולנהל סוכני AI. בעלי מערכת (Owner) מקבלים הכול אוטומטית.",
    when: "כשצריך לתת או להסיר גישה של משתמש לסוכני AI.",
    readOnly: false,
    role: AGENTS_MANAGE,
  },
  "/knowledge": {
    what: "בסיס ידע מאושר.",
    does: "מאמרים מאושרים שנכללים אוטומטית בהקשר של ניתוח ה-AI — נותנים ביטחון גבוה יותר מהיסטוריה גולמית.",
    when: "כדי לאשר או לנהל את המידע שה-AI מסתמך עליו בביטחון גבוה.",
    readOnly: false,
    role: AGENTS_MANAGE,
  },
  "/connections": {
    what: "חיבורים ורישום כלים.",
    does: "מציג את מצב האינטגרציות החיצוניות של המערכת.",
    when: "לבדוק אם חיבור חיצוני תקין או דורש טיפול.",
    readOnly: true,
    role: ADMIN_ONLY,
  },
  "/platform-health": {
    what: "בריאות הפלטפורמה.",
    does: "מצב שרת, גיבויים, חיבורים וסוכני AI במקום אחד.",
    when: "בדיקת תקינות כללית של התשתית.",
    readOnly: true,
    role: ADMIN_ONLY,
  },
  "/employee-builder": {
    what: "אשף בניית עובד דיגיטלי.",
    does: "הגדרת סוכן AI חדש כולל מדיניות אישור מלאה לפני שהוא פעיל.",
    when: "כשרוצים ליצור סוכן AI חדש.",
    readOnly: false,
    role: ADMIN_ONLY,
  },
  "/scheduled-checks": {
    what: "בדיקות מתוזמנות.",
    does: "הגדרת בדיקות שרצות אוטומטית בזמנים קבועים.",
    when: "כדי שפעולה חוזרת תתבצע בלי הפעלה ידנית כל פעם.",
    readOnly: false,
    role: ADMIN_ONLY,
  },
  "/bug-hunter": {
    what: "ציד באגים.",
    does: "זיהוי דפוסי תקלות חוזרות מתוך קריאות IT.",
    when: "לאתר בעיה שחוזרת על עצמה אצל כמה לקוחות.",
    readOnly: true,
    role: ADMIN_ONLY,
  },
  "/backup-guardian": {
    what: "שומר גיבויים.",
    does: "מעקב תקינות גיבויים, ויצירת קריאות שירות אוטומטית כשגיבוי נכשל.",
    when: "לוודא שגיבויי לקוחות תקינים.",
    readOnly: false,
    role: ADMIN_ONLY,
  },
  "/alerts": {
    what: "התראות AI.",
    does: "ריכוז התראות שנוצרו על-ידי סוכני AI על מצבים שדורשים תשומת לב.",
    when: "לבדוק אם יש התראה פעילה שטרם טופלה.",
    readOnly: true,
    role: ADMIN_ONLY,
  },
  "/skills": {
    what: "מיומנויות סוכנים.",
    does: "ציוני ביצוע לפי ריצות שהושלמו — מחושב בזמן אמת מנתוני ריצות קיימות.",
    when: "להעריך כמה טוב סוכן AI מסוים מבצע את תפקידו.",
    readOnly: true,
    role: ADMIN_ONLY,
  },
  "/feedback": {
    what: "איכות AI.",
    does: "ניתוח תיקונים שטכנאים ביצעו על תוצרי AI — משמש כסיגנל למידה לשיפור הסוכנים.",
    when: "לבחון היכן ה-AI טועה ואיפה יש מקום לשיפור.",
    readOnly: true,
    role: ADMIN_ONLY,
  },
  "/tech-scout": {
    what: "סיור טכנולוגיות.",
    does: "סריקת גרסאות חבילות, איתור עדכונים נדרשים והמלצות טכנולוגיות.",
    when: "לבדוק אם יש עדכוני תוכנה או המלצות רלוונטיות.",
    readOnly: true,
    role: ADMIN_ONLY,
  },
  "https://command.osinet.ai": {
    what: "קישור חיצוני לכלי Command Osinet.",
    does: "פותח כלי חיצוני בכרטיסייה נפרדת — מחוץ למערכת Prodigy.",
    when: "כשצריך את הכלי החיצוני הזה במסגרת העבודה.",
    external: true,
  },
  "/activity-log": {
    what: "יומן פעולות.",
    does: "היסטוריית פעולות שבוצעו במערכת, לצורכי מעקב ותיעוד.",
    when: "לבדוק מי עשה מה ומתי.",
    readOnly: true,
  },
  "/notification-settings": {
    what: "הגדרות התראות אישיות.",
    does: "שליטה על אילו התראות מתקבלות ובאיזה אופן.",
    when: "כדי להתאים את ההתראות להעדפה האישית.",
    readOnly: false,
  },
  "/settings": {
    what: "הגדרות מערכת כלליות.",
    does: "תצורה כללית של המערכת.",
    when: "שינויים שמשפיעים על כלל המשתמשים.",
    readOnly: false,
    role: ADMIN_ONLY,
  },
};
